import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Eye, EyeOff, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PERSONAS, ADMIN_QUICK, BRAND_LOGO } from "../lib/personas";
import Avatar from "../components/Avatar";

export default function Login() {
  const { user, login, loginAsPersona, loginAsAdmin, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = new URLSearchParams(loc.search).get("returnTo") || "/";

  if (user) return <Navigate to={returnTo} replace />;
  if (user === undefined) return null;

  const doSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setShake(true);
      setTimeout(() => setShake(false), 300);
      return;
    }
    setBusy(true);
    let ok = false;
    try {
      ok = await login(email, password, { remember });
    } finally {
      setBusy(false);
    }
    if (ok) nav(returnTo, { replace: true });
    else { setShake(true); setTimeout(() => setShake(false), 300); }
  };

  const handlePersona = async (personaKey) => {
    setBusy(true);
    let ok = false;
    try {
      ok = await loginAsPersona(personaKey, { remember });
    } finally {
      setBusy(false);
    }
    if (ok) nav(returnTo, { replace: true });
  };

  const handleAdminQuick = async () => {
    setBusy(true);
    let ok = false;
    try {
      ok = await loginAsAdmin({ remember });
    } finally {
      setBusy(false);
    }
    if (ok) nav(returnTo, { replace: true });
    else { setShake(true); setTimeout(() => setShake(false), 300); }
  };

  return (
    <div className="h-screen w-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* LEFT PANEL — Brand showcase */}
      <div className="lg:w-1/2 h-[30vh] lg:h-full login-terracotta-panel flex items-center justify-center p-8 lg:p-12 relative">
        <div className="relative z-10 flex flex-col items-center text-center max-w-[480px]">
          <img
            src={BRAND_LOGO}
            alt="Maisonéa"
            className="w-24 h-24 logo-white object-contain"
          />
          <h1 className="font-heading text-[56px] leading-tight font-semibold text-cream mt-12">
            Maisonéa
          </h1>
          <p className="font-heading italic text-[24px] text-cream/80 mt-4">
            L'humain au cœur de la maison
          </p>
          <div className="flex items-center gap-2 mt-16" aria-hidden="true">
            <span className="w-6 h-px bg-cream/60" />
            <span className="w-6 h-px bg-cream/60" />
            <span className="w-6 h-px bg-cream/60" />
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-8 left-0 right-0 text-center text-[14px] text-cream/70 font-sans">
          Écosystème Maisonéa Connect
        </div>
      </div>

      {/* RIGHT PANEL — Login form */}
      <div className="flex-1 bg-cream flex items-center justify-center overflow-y-auto p-8 lg:p-12">
        <div className={`w-full max-w-[400px] animate-fade-in-up ${shake ? "shake" : ""}`} data-testid="login-form-panel">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-terracotta-light text-terracotta text-[13px] font-medium">
              SIRH Maisonéa
            </span>
          </div>

          <h2 className="font-heading text-[36px] font-semibold text-warm-dark leading-tight">Bienvenue</h2>
          <p className="text-[16px] text-warm-dark/70 mt-2 mb-8">Connectez-vous pour accéder à vos outils.</p>

          <form onSubmit={doSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[14px] font-medium text-warm-dark mb-1.5 block">Adresse e-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom.nom@maisonea.fr"
                className="h-11 bg-off-white border-sand focus-visible:border-terracotta focus-visible:ring-terracotta/15 rounded-lg"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[14px] font-medium text-warm-dark mb-1.5 block">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 bg-off-white border-sand focus-visible:border-terracotta focus-visible:ring-terracotta/15 rounded-lg pr-10"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-dark/60 hover:text-warm-dark"
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Masquer" : "Afficher"}
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-[14px] text-warm-dark cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                Se souvenir de moi
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-[14px] font-medium text-terracotta hover:text-terracotta-dark"
                data-testid="login-forgot-link"
              >
                Mot de passe oublié ?
              </button>
            </div>

            {error && (
              <div className="text-[14px] text-error bg-error/5 border border-error/20 px-3 py-2 rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-12 bg-terracotta hover:bg-terracotta-dark text-cream rounded-lg text-[16px] font-semibold"
            >
              {busy ? <Loader2 size={18} className="animate-spin" strokeWidth={1.5} /> : "Se connecter"}
            </Button>
          </form>

          <div className="mt-8 mb-6 divider-text text-[14px] text-warm-dark/60">ou</div>

          <div>
            <div className="text-[14px] font-medium text-warm-dark/70 mb-3">
              Connexion rapide
            </div>
            <div className="flex flex-col gap-2 stagger" data-testid="personas-list">
              <button
                type="button"
                onClick={handleAdminQuick}
                className="persona-card h-14 w-full bg-terracotta-light/60 border border-terracotta/40 rounded-lg px-3 flex items-center gap-3 text-left"
                data-testid="persona-login-admin"
                disabled={busy}
              >
                <div className="w-10 h-10 rounded-full bg-terracotta text-cream flex items-center justify-center shrink-0">
                  <ShieldCheck size={18} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-warm-dark truncate flex items-center gap-1.5">
                    {ADMIN_QUICK.display_name}
                    <span className="inline-flex items-center px-1.5 py-0 rounded-full bg-terracotta text-cream text-[10px] font-semibold tracking-wide uppercase">
                      Admin
                    </span>
                  </div>
                  <div className="text-[12px] text-warm-dark/60 truncate">{ADMIN_QUICK.role} · {ADMIN_QUICK.site}</div>
                </div>
                <ArrowRight size={16} strokeWidth={1.5} className="text-terracotta shrink-0" />
              </button>
              {PERSONAS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePersona(p.key)}
                  className="persona-card h-14 w-full bg-off-white border border-sand rounded-lg px-3 flex items-center gap-3 text-left"
                  data-testid={`persona-login-${p.key}`}
                  disabled={busy}
                >
                  <Avatar
                    name={p.display_name}
                    seed={p.key}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-warm-dark truncate">{p.display_name}</div>
                    <div className="text-[12px] text-warm-dark/60 truncate">{p.role} · {p.site}</div>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.5} className="text-terracotta shrink-0" />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center text-[12px] text-warm-dark/50 space-y-0.5">
            <div>Powered by Maisonéa Connect</div>
            <div>v1.0 · Environnement de démo</div>
          </div>
        </div>
      </div>

      {/* Forgot password modal */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-dialog" className="bg-off-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-warm-dark">Mot de passe oublié</DialogTitle>
          </DialogHeader>
          <p className="text-[14px] text-warm-dark/80 leading-relaxed">
            Cette fonctionnalité est désactivée pour la démo. Contactez votre administrateur Maisonéa.
          </p>
          <DialogFooter>
            <Button onClick={() => setForgotOpen(false)} className="bg-terracotta hover:bg-terracotta-dark text-cream" data-testid="forgot-close">
              J'ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
